import { useAuthContext } from 'contexts';
import { useState } from 'react';
import { careGet, carePost } from 'services';

type FoodTriageSurveyResponseType = {
  questionText: string;
  questionId: string;
  answerSelected: {
    answerText: string | boolean;
    answerId: string;
  }[];
};

type FoodTriageSurveyPayload = {
  triageCategory: string;
  triageRequest: string;
  descriptions: string[];
  item: FoodTriageSurveyResponseType[];
  /** Primary order with the issue; triage doc is keyed by this id. */
  orderId: string;
  /** All order ids that should receive the same food-order updates (primary + linked). Optional; backend also reads linked ids from the survey item keyed by `TRIAGE_QUESTION_ID.LINKED_ORDER_IDS` in `foodOrderTriageModalV2/fixtures`. */
  orderIds?: string[];
  orgId: string;
  patientId: string;
  reship: boolean;
  instacartOrder: boolean;
  createTriageTask: boolean;
  draft: boolean;
  highPriorityTriage: boolean;
  assignTo: string;
  noFaultToVendor: boolean;
};

/**
 * Distinguishes draft vs final triage POSTs so the UI can show a spinner only on the button that was clicked.
 */
export type FoodTriagePostSubmitKind = 'draft' | 'final';

export const useFoodOrderTriageSurveyV2 = () => {
  const { practitionerId, token } = useAuthContext();
  const [postLoadingKind, setPostLoadingKind] = useState<FoodTriagePostSubmitKind | null>(null);
  const [isGetLoading, setIsGetLoading] = useState<boolean>(false);

  const postTriageSurveyResponse = async (
    foodTriageSurveyPayload: FoodTriageSurveyPayload,
    options?: { submitKind?: FoodTriagePostSubmitKind }
  ) => {
    const url = '/hooks/post-triage-survey-response';
    const payload = {
      ...foodTriageSurveyPayload,
      practitionerId,
    };
    const submitKind = options?.submitKind ?? 'final';
    try {
      setPostLoadingKind(submitKind);
      await carePost(token, url, payload);
      setPostLoadingKind(null);
    } catch (err) {
      setPostLoadingKind(null);
      throw err;
    }
  };

  /**
   * `TRecord` is caller-supplied, not defaulted to `index.tsx`'s
   * `TriageSurveyFetchedRecord` — that would make this hook depend on its own
   * consumer. `services/care-hooks`'s `careGet` threads it through as an
   * unchecked claim; see the note there.
   *
   * The `undefined` in the return is the `catch` arm, which swallows the error
   * and resolves with nothing. Existing behaviour; `pickLatestTriageRecord`
   * already takes `rows: … | undefined`.
   */
  const getTriageSurveyResponse = async <TRecord,>(orderId: string) => {
    const query = `orderId=${orderId}`;
    const url = `/hooks/fetch-triage-survey-response/${query}`;

    try {
      setIsGetLoading(true);
      const data = await careGet<TRecord[]>(url, token);

      setIsGetLoading(false);
      return data;
    } catch {
      setIsGetLoading(false);
    }
  };

  return {
    isGetLoading,
    postLoadingKind,
    getTriageSurveyResponse,
    postTriageSurveyResponse,
  };
};
